import { Actor, CollisionType, Color, Circle, Engine, vec, Vector, Collider, Shape, Animation } from "excalibur";
import { PLAYER, VISUALS } from "../config";
import { Virus } from "./Virus";
import { MacrophageCollision } from "../systems/CollisionGroups";
import { Resources } from "../resources";
import { createPackedAnimation } from "../utils/AtlasLoader";

/**
 * Macrophage - The Player Character
 * 
 * A large immune cell that the player controls by touching/clicking.
 * Represents Riley's immune system fighting off the influenza infection.
 * 
 * Controls: Touch/click anywhere to move the macrophage toward that position.
 * This "follow finger" control scheme is accessible for a sick player.
 */
export class Macrophage extends Actor {
    public hp: number = PLAYER.MAX_HP;
    public maxHp: number = PLAYER.MAX_HP;
    public xp: number = 0;
    public level: number = 1;
    public xpToNextLevel: number = 5;

    private moveSpeed: number = PLAYER.MOVE_SPEED;
    private isInvulnerable: boolean = false;
    private invulnerabilityTime: number = 0;
    private readonly INVULNERABILITY_DURATION = 1000; // ms after taking damage

    // Animations
    private walkAnimation: Animation | null = null;
    private shootAnimation: Animation | null = null;
    private isShooting: boolean = false;
    private shootAnimationTimer: number = 0;
    private readonly SHOOT_ANIMATION_DURATION = 500; // ms

    constructor() {
        super({
            name: 'Macrophage',
            pos: vec(VISUALS.WORLD_WIDTH / 2, VISUALS.WORLD_HEIGHT / 2),
            radius: PLAYER.RADIUS,
            color: Color.fromHex(VISUALS.COLORS.PLAYER_CYTOPLASM),
            // Active allows being pushed by physics
            collisionType: CollisionType.Active,
            // Collides with walls and enemies
            collisionGroup: MacrophageCollision,
        });
    }

    override onInitialize(engine: Engine): void {
        // Set up circular collider and restore collision group manually
        const collider = Shape.Circle(PLAYER.RADIUS) as any;
        collider.group = MacrophageCollision;
        this.collider.set(collider);

        // Load animations using AtlasLoader
        this.loadAnimations();
    }

    private loadAnimations(): void {
        try {
            // Create walk animation
            this.walkAnimation = createPackedAnimation(
                Resources.MacrophageWalkImage,
                Resources.MacrophageWalkJson,
                10 // 10 FPS
            );

            // Create shoot animation
            this.shootAnimation = createPackedAnimation(
                Resources.MacrophageShootImage,
                Resources.MacrophageShootJson,
                12 // 12 FPS
            );

            // Scale animations to fit player radius (1.5x size increase)
            const targetSize = PLAYER.RADIUS * 2 * 1.5;
            if (this.walkAnimation.width > 0) {
                const scale = targetSize / this.walkAnimation.width;
                this.walkAnimation.scale = vec(scale, scale);
            }
            if (this.shootAnimation.width > 0) {
                const scale = targetSize / this.shootAnimation.width;
                this.shootAnimation.scale = vec(scale, scale);
            }

            // Use walk animation by default
            this.graphics.use(this.walkAnimation);
        } catch (error) {
            console.warn('Failed to load macrophage animations, using fallback', error);
            this.useFallbackGraphic();
        }
    }

    private useFallbackGraphic(): void {
        // Outer membrane with stroke (fallback)
        const membrane = new Circle({
            radius: PLAYER.RADIUS,
            color: Color.fromHex(VISUALS.COLORS.PLAYER_CYTOPLASM),
            strokeColor: Color.fromHex(VISUALS.COLORS.PLAYER_MEMBRANE),
            lineWidth: 2,
        });
        this.graphics.use(membrane);
    }

    override onPreUpdate(engine: Engine, delta: number): void {
        // Update invulnerability timer
        if (this.isInvulnerable) {
            this.invulnerabilityTime -= delta;
            if (this.invulnerabilityTime <= 0) {
                this.isInvulnerable = false;
                this.graphics.opacity = 1;
            } else {
                // Flash effect during invulnerability
                this.graphics.opacity = 0.5 + Math.sin(Date.now() / 50) * 0.3;
            }
        }

        // Update shoot animation timer
        if (this.isShooting) {
            this.shootAnimationTimer -= delta;
            if (this.shootAnimationTimer <= 0) {
                this.isShooting = false;
                if (this.walkAnimation) {
                    this.graphics.use(this.walkAnimation);
                }
            }
        }

        // Mobile "Follow Finger" Control Logic
        const pointer = engine.input.pointers.primary;
        const lastPos = pointer.lastWorldPos;

        // Check if there's active pointer input
        const hasActivePointer = engine.input.pointers.isDown(0);

        if (hasActivePointer && lastPos) {
            const direction = lastPos.sub(this.pos);

            // Deadzone check: Only move if finger is far enough away
            if (direction.size > PLAYER.DEADZONE) {
                this.vel = direction.normalize().scale(this.moveSpeed);
            } else {
                this.vel = Vector.Zero;
            }
        } else {
            // Stop when not touching
            this.vel = Vector.Zero;
        }

        // Soft clamp to world bounds (walls handle the physics, this is backup)
        this.pos.x = Math.max(PLAYER.RADIUS, Math.min(VISUALS.WORLD_WIDTH - PLAYER.RADIUS, this.pos.x));
        this.pos.y = Math.max(PLAYER.RADIUS, Math.min(VISUALS.WORLD_HEIGHT - PLAYER.RADIUS, this.pos.y));
    }

    /**
     * Play the shooting animation (for Cytokine Storm)
     */
    public playShootAnimation(): void {
        if (this.shootAnimation) {
            this.isShooting = true;
            this.shootAnimationTimer = this.SHOOT_ANIMATION_DURATION;
            this.shootAnimation.reset();
            this.graphics.use(this.shootAnimation);
        }
    }

    /**
     * Handle collision with other actors
     */
    override onCollisionStart(_self: Collider, other: Collider): void {
        // Check if we collided with an active virus
        if (other.owner instanceof Virus && other.owner.isPoolActive()) {
            this.takeDamage(other.owner.getDamage());
        }
    }

    /**
     * Take damage from a virus
     */
    public takeDamage(amount: number): void {
        if (this.isInvulnerable) return;

        this.hp = Math.max(0, this.hp - amount);
        this.isInvulnerable = true;
        this.invulnerabilityTime = this.INVULNERABILITY_DURATION;
    }

    /**
     * Collect XP from an antigen
     */
    public collectXP(amount: number): boolean {
        this.xp += amount;

        if (this.xp >= this.xpToNextLevel) {
            this.levelUp();
            return true;
        }
        return false;
    }

    /**
     * Level up - "Clonal Expansion"
     */
    private levelUp(): void {
        this.level++;
        this.xp = 0;
        this.xpToNextLevel = Math.floor(this.xpToNextLevel * 1.5);
        this.hp = Math.min(this.maxHp, this.hp + 20);
    }

    /**
     * Heal the player (from collecting Lipid Rafts)
     */
    public heal(amount: number): void {
        this.hp = Math.min(this.maxHp, this.hp + amount);
    }

    /**
     * Check if player is dead
     */
    public isDead(): boolean {
        return this.hp <= 0;
    }

    /**
     * Respawn the player
     */
    public respawn(): void {
        this.hp = this.maxHp;
        this.pos = vec(VISUALS.WORLD_WIDTH / 2, VISUALS.WORLD_HEIGHT / 2);
        this.isInvulnerable = true;
        this.invulnerabilityTime = 2000;
    }

    /**
     * Full reset for new game
     */
    public reset(): void {
        this.level = 1;
        this.xp = 0;
        this.xpToNextLevel = 5;
        this.maxHp = PLAYER.MAX_HP;
        this.isShooting = false;
        this.shootAnimationTimer = 0;
        if (this.walkAnimation) {
            this.graphics.use(this.walkAnimation);
        }
        this.respawn();
    }
}
